(() => {
  const cleanups = new WeakMap();

  const enhanceEditorialIcons = async (body, signal) => {
    if (!document.fonts) return;
    try {
      const faces = await document.fonts.load('20px "Material Symbols Outlined"', 'check');
      if (!faces.length || signal.aborted || !body.isConnected) return;
    } catch {
      return;
    }

    body.querySelectorAll('.fp-ratgeber-status--yes > .fp-ratgeber-status__symbol').forEach((icon) => {
      icon.classList.add('material-symbols-outlined');
      icon.textContent = 'check';
    });
    const benefitIcons = new Set(['receipt_long', 'tune', 'groups', 'print', 'wifi', 'payments', 'edit_square', 'touch_long']);
    body.querySelectorAll('.fp-ratgeber-benefit-icon[data-ratgeber-icon]').forEach((icon) => {
      const name = icon.dataset.ratgeberIcon;
      if (!benefitIcons.has(name)) return;
      icon.classList.add('material-symbols-outlined');
      icon.textContent = name;
    });
  };

  const normalizeFaq = (body) => {
    body.querySelectorAll('h2').forEach((heading) => {
      const label = heading.textContent.trim().replace(/^\d+\.\s*/, '');
      if (!/häufige fragen|^faq\b/i.test(label)) return;
      let node = heading.nextElementSibling;
      let list;
      while (node && node.tagName !== 'H2') {
        if (node.tagName !== 'H3') {
          node = node.nextElementSibling;
          continue;
        }
        const answerNodes = [];
        let next = node.nextSibling;
        while (next && !(next.nodeType === 1 && /^(H2|H3)$/.test(next.tagName))) {
          answerNodes.push(next);
          next = next.nextSibling;
        }
        if (!answerNodes.some((part) => part.textContent.trim())) {
          node = next;
          continue;
        }
        if (!list) {
          list = document.createElement('div');
          list.className = 'faq-list';
          node.before(list);
        }
        const item = document.createElement('details');
        item.className = 'faq-item';
        const summary = document.createElement('summary');
        const answer = document.createElement('div');
        answer.className = 'faq-answer';
        summary.append(node);
        answer.append(...answerNodes);
        item.append(summary, answer);
        list.append(item);
        node = next;
      }
    });
    body.querySelectorAll('.faq-list details').forEach((item) => {
      item.classList.add('faq-item');
      if (item.querySelector(':scope > .faq-answer')) return;
      const answer = document.createElement('div');
      answer.className = 'faq-answer';
      [...item.childNodes].filter((node) => node.nodeName !== 'SUMMARY').forEach((node) => answer.append(node));
      item.append(answer);
    });
  };

  const organizeEditorialDetails = (body) => {
    body.querySelectorAll('.faq-list').forEach((group) => {
      const items = [...group.children].filter((node) => node.matches('details'));
      if (items.length !== 1) return;
      // A single explanation should read as part of its own subject, without an extra click.
      const item = items[0];
      const summary = item.querySelector(':scope > summary');
      const answer = item.querySelector(':scope > .faq-answer');
      if (!summary || !answer) return;
      const heading = document.createElement('h3');
      heading.className = 'fp-ratgeber-v2__detail-heading';
      heading.textContent = summary.textContent.trim();
      if (item.id) heading.id = item.id;
      item.replaceWith(heading, ...answer.childNodes);
      group.replaceWith(...group.childNodes);
    });
    body.querySelectorAll('.fp-ratgeber-example').forEach((example) => {
      if (example.querySelector('.fp-ratgeber-v2__example-label')) return;
      const paragraph = example.querySelector(':scope > p:first-child');
      const strong = paragraph?.querySelector(':scope > strong:first-child');
      const prefix = strong?.textContent.match(/^(Fiktives\s+)?(?:Praxisbeispiel|Beispiel)\s*:\s*/i);
      const label = document.createElement('p');
      label.className = 'fp-ratgeber-v2__example-label';
      label.textContent = 'Praxisbeispiel';
      if (prefix) {
        const title = strong.textContent.slice(prefix[0].length);
        const headingOnly = paragraph.textContent.trim() === strong.textContent.trim();
        strong.textContent = title;
        if (!title) strong.remove();
        else if (headingOnly) paragraph.remove();
        if (!paragraph.textContent.trim()) paragraph.remove();
      }
      example.prepend(label);
    });
  };

  const init = (root) => {
    if (cleanups.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const observers = [];
    let copyResetTimer;
    const share = root.querySelector('[data-ratgeber-share]');
    const copyButton = share?.querySelector('[data-ratgeber-copy-link]');
    if (copyButton && navigator.clipboard?.writeText) {
      share.hidden = false;
      const label = copyButton.querySelector('[data-copy-label]');
      const status = share.querySelector('[data-copy-status]');
      const originalLabel = label.textContent;
      copyButton.addEventListener('click', async () => {
        copyButton.disabled = true;
        clearTimeout(copyResetTimer);
        status.textContent = '';
        try {
          await navigator.clipboard.writeText(copyButton.dataset.copyUrl);
          if (signal.aborted) return;
          label.textContent = copyButton.dataset.copySuccess;
          status.textContent = copyButton.dataset.copySuccess;
        } catch {
          if (signal.aborted) return;
          label.textContent = copyButton.dataset.copyError;
          status.textContent = copyButton.dataset.copyError;
        } finally {
          copyButton.disabled = false;
          if (!signal.aborted) copyResetTimer = setTimeout(() => {
            label.textContent = originalLabel;
            status.textContent = '';
          }, 4000);
        }
      }, { signal });
    }
    const body = root.querySelector('[data-ratgeber-body]');
    const mobile = window.matchMedia('(max-width: 899px)');
    const topics = root.querySelector('[data-ratgeber-topics]');
    const chapters = root.querySelectorAll('[data-ratgeber-chapter]');
    const setTopics = () => {
      if (topics) topics.open = !mobile.matches;
      chapters.forEach((chapter) => {
        chapter.open = chapter.hasAttribute('data-current-chapter');
      });
    };
    setTopics();
    mobile.addEventListener('change', setTopics, { signal });

    let keyboardNavigation = false;
    document.addEventListener('keydown', () => {
      keyboardNavigation = true;
      root.querySelectorAll('.fp-ratgeber-v2__pointer-target').forEach((target) => target.classList.remove('fp-ratgeber-v2__pointer-target'));
    }, { signal });
    document.addEventListener('pointerdown', () => { keyboardNavigation = false; }, { signal });

    if (body) {
      body.querySelectorAll('h2[id]').forEach((heading) => { heading.tabIndex = -1; });
      normalizeFaq(body);
      organizeEditorialDetails(body);
      enhanceEditorialIcons(body, signal);
      const costTemplate = root.querySelector('template[data-ratgeber-cost-summary]');
      if (costTemplate && !body.querySelector('.fp-ratgeber-v2__cost-summary')) {
        const source = [...body.querySelectorAll('p')].find((paragraph) =>
          paragraph.textContent.includes('Die externen Kosten betragen 3.840 Euro') &&
          paragraph.textContent.includes('6.000 Euro')
        );
        if (source) source.after(costTemplate.content.cloneNode(true));
      }

      const reading = root.querySelector('.fp-ratgeber-v2__reading');
      reading?.querySelectorAll('table > caption').forEach((caption) => caption.remove());
      reading?.querySelectorAll('figure').forEach((figure) => {
        if (figure.querySelector('img')) figure.querySelectorAll('figcaption').forEach((caption) => caption.remove());
      });

      body.querySelectorAll('table').forEach((table, index) => {
        table.querySelectorAll('tbody tr').forEach((row) => {
          const rowText = row.textContent.replace(/\s+/g, ' ').trim();
          if (rowText.includes('13 Euro pro Monat') && rowText.includes('−17 Euro pro Monat')) row.classList.add('fp-ratgeber-v2__scenario-results');
          [...row.cells].forEach((cell) => {
            if (/^\s*[−+\-]?[\d.,]+\s*(?:€|Euro\b)/i.test(cell.textContent)) cell.classList.add('fp-ratgeber-v2__money');
          });
          if (/^(Gesamtkosten|Gesamt|Summe|Rechnerischer Vorteil)\s*:?$/i.test(row.cells[0]?.textContent.trim())) row.classList.add('fp-ratgeber-v2__total');
        });
        table.querySelectorAll('thead th').forEach((heading) => {
          const cells = [...table.querySelectorAll('tbody tr')].map((row) => row.cells[heading.cellIndex]);
          if (cells.length && cells.every((cell) => cell?.classList.contains('fp-ratgeber-v2__money'))) heading.classList.add('fp-ratgeber-v2__money');
          if (!table.querySelector('.fp-ratgeber-status') && cells.length && cells.every((cell) => /^\s*[−+\-]?\d/.test(cell?.textContent || ''))) {
            heading.classList.add('fp-ratgeber-v2__money');
            cells.forEach((cell) => cell.classList.add('fp-ratgeber-v2__money'));
          }
        });
        let region = table.parentElement;
        if (!region.matches('.table-wrap,.table-scroll,.fp-knowledge__table,.fp-ratgeber-v2__table-region')) {
          region = document.createElement('div');
          table.before(region);
          region.append(table);
        }
        region.classList.add('fp-ratgeber-v2__table-region');
        region.setAttribute('role', 'region');
        if (!region.hasAttribute('aria-label')) {
          region.setAttribute('aria-label', table.caption?.textContent.trim() || 'Vergleichstabelle');
        }
        let hint = region.previousElementSibling;
        if (!hint?.classList.contains('fp-ratgeber-v2__table-hint')) {
          hint = document.createElement('p');
          hint.className = 'fp-ratgeber-v2__table-hint';
          hint.id = `ratgeber-table-${root.dataset.ratgeberId}-${index}`;
          hint.textContent = 'Tabelle seitlich scrollen';
          region.before(hint);
        }
        const updateOverflow = () => {
          const overflow = region.scrollWidth > region.clientWidth + 2;
          hint.hidden = !overflow;
          if (overflow) {
            region.tabIndex = 0;
            region.setAttribute('aria-describedby', hint.id);
          } else {
            region.removeAttribute('tabindex');
            region.removeAttribute('aria-describedby');
          }
        };
        updateOverflow();
        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(updateOverflow);
          observer.observe(region);
          observer.observe(table);
          observers.push(observer);
        }
      });
    }

    const jumpLinks = [...root.querySelectorAll('.fp-ratgeber-v2__jump a')];
    const sections = jumpLinks.map((link) => document.getElementById(link.hash.slice(1)));
    let scrollFrame;
    const updateJump = () => {
      scrollFrame = undefined;
      let current = '';
      sections.forEach((section) => {
        if (!section) return;
        const offset = parseFloat(getComputedStyle(section).scrollMarginTop) || 136;
        if (section.getBoundingClientRect().top <= offset + 16) current = `#${section.id}`;
      });
      jumpLinks.forEach((link) => {
        if (link.hash === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };
    const onScroll = () => {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(updateJump);
    };
    if (jumpLinks.length) {
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', onScroll, { passive: true, signal });
      updateJump();
    }

    const openHashTarget = () => {
      if (!window.location.hash) return;
      let id;
      try { id = decodeURIComponent(window.location.hash.slice(1)); } catch { return; }
      const target = document.getElementById(id);
      if (!target || !root.contains(target)) return;
      if (target.matches('[data-ratgeber-body] h2')) {
        target.classList.toggle('fp-ratgeber-v2__pointer-target', !keyboardNavigation);
        target.focus({ preventScroll: true });
      }
      let details = target.closest('details');
      while (details) {
        details.open = true;
        details = details.parentElement.closest('details');
      }
    };
    openHashTarget();
    window.addEventListener('hashchange', openHashTarget, { signal });
    cleanups.set(root, () => {
      controller.abort();
      clearTimeout(copyResetTimer);
      observers.forEach((observer) => observer.disconnect());
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      cleanups.delete(root);
    });
  };

  document.querySelectorAll('[data-ratgeber-root]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-ratgeber-root]').forEach(init);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ratgeber-root]').forEach((root) => cleanups.get(root)?.());
  });
})();
